import { Box, HStack, PartThumbnail, Text } from '@frontend/components';

import HighlightOrDisplay from './HighlightOrDisplay';

const ThumbnailContainer = ({ children }) => (
  <Box
    alignItems="center"
    borderLeftRadius="md"
    bg="linear-gradient(150deg, #fff 0%, #cccccc 100%)"
    display="flex"
    flexDirection="column"
    height="100%"
    justifyContent="center"
    marginBottom="-1px"
    position="relative"
    width={11}
  >
    {children}
  </Box>
);

const ThumbnailWarning = ({ text, color }) => (
  <Box
    background={color}
    top="0"
    borderTopLeftRadius="md"
    height={3}
    position="absolute"
    textAlign="center"
    width="100%"
  >
    <Box
      as="span"
      color="white"
      fontSize="sm"
      fontWeight="heavy"
      letterSpacing="1px"
      textTransform="uppercase"
    >
      {text}
    </Box>
  </Box>
);

const CellWarning = ({ text, color }) => (
  <Box
    background={color}
    top="0"
    borderTopRightRadius="md"
    height={3}
    textAlign="center"
    width="100%"
  >
    <Box
      as="span"
      color="white"
      fontSize="sm"
      fontWeight="heavy"
      letterSpacing="1px"
      textTransform="uppercase"
    >
      {text}
    </Box>
  </Box>
);

const AssemblyHint = () => (
  <Box
    bg="interaction.main"
    bottom={0.5}
    right={0.5}
    borderRadius="sm"
    height="18px"
    width="18px"
    textAlign="center"
    position="absolute"
    display="flex"
  >
    <Box
      as="span"
      color="white"
      fontSize="sm"
      fontWeight="heavy"
      margin="auto"
      lineHeight="0px"
    >
      A
    </Box>
  </Box>
);

const PartCell = ({
  primaryFileUuid,
  filename,
  containsVirus,
  partResult, // { data: {}, highlight: {} }
  href,
  customWarning: {
    text: customWarningText = null,
    color: customWarningColor = null,
  } = {},
}) => {
  const {
    partNumber,
    revision,
    partName,
    exportControlled: isExportControlled,
    isAssembly,
  } = partResult?.data ?? {};
  const hasPartNoOrRev = !!partNumber || !!revision;

  const partAndRev = hasPartNoOrRev ? (
    <table>
      <colgroup>
        <col width="20%" />
        <col />
      </colgroup>
      <tbody>
        <tr>
          <td style={{ verticalAlign: 'top' }}>
            <Text
              textTransform="uppercase"
              fontFamily="data"
              fontSize="sm"
              fontWeight="heavy"
              color="feature.secondary"
              whiteSpace="nowrap"
            >
              PART #
            </Text>
          </td>
          <td style={{ paddingLeft: '8px' }}>
            <Text
              color="feature.primary"
              fontFamily="data"
              fontSize="sm"
              fontWeight="heavy"
              whiteSpace="normal"
              wordBreak="break-all"
            >
              {partNumber ? (
                <HighlightOrDisplay
                  result={partResult}
                  field="partNumber"
                  textProps={{ fontWeight: 'heavy' }}
                />
              ) : (
                '--'
              )}
            </Text>
          </td>
        </tr>
        <tr>
          <td style={{ verticalAlign: 'top' }}>
            <Text
              textTransform="uppercase"
              fontFamily="data"
              fontSize="sm"
              fontWeight="heavy"
              color="feature.secondary"
              whiteSpace="nowrap"
            >
              REV
            </Text>
          </td>
          <td style={{ paddingLeft: '8px' }}>
            <Text
              color="feature.primary"
              fontFamily="data"
              fontSize="sm"
              fontWeight="heavy"
              whiteSpace="nowrap"
            >
              {revision ? (
                <HighlightOrDisplay
                  result={partResult}
                  field="revision"
                  textProps={{ fontWeight: 'heavy' }}
                />
              ) : (
                '--'
              )}
            </Text>
          </td>
        </tr>
      </tbody>
    </table>
  ) : (
    <Box display="flex" flexDir="row" justifyContent="left">
      <HStack flex="0.5 0 auto" mr={1}>
        <Text
          textTransform="uppercase"
          fontFamily="data"
          fontSize="sm"
          fontWeight="heavy"
          color="feature.secondary"
          whiteSpace="nowrap"
        >
          PART #
        </Text>
        <Text
          color="feature.primary"
          fontFamily="data"
          fontSize="sm"
          fontWeight="heavy"
          whiteSpace="normal"
          wordBreak="break-all"
        >
          {partNumber ? (
            <HighlightOrDisplay
              result={partResult}
              field="partNumber"
              useDataFont
            />
          ) : (
            '--'
          )}
        </Text>
      </HStack>
      <HStack flex="1 0 auto">
        <Text
          textTransform="uppercase"
          fontFamily="data"
          fontSize="sm"
          fontWeight="heavy"
          color="feature.secondary"
          whiteSpace="nowrap"
        >
          REV
        </Text>
        <Text
          color="feature.primary"
          fontFamily="data"
          fontSize="sm"
          fontWeight="heavy"
          whiteSpace="normal"
          wordBreak="break-all"
        >
          {revision ? (
            <HighlightOrDisplay
              result={partResult}
              field="revision"
              useDataFont
            />
          ) : (
            '--'
          )}
        </Text>
      </HStack>
    </Box>
  );

  const linkProps = href
    ? {
        as: 'a',
        href: href,
        _hover: {
          bg: 'interaction.surfaceHover',
        },
        _focus: {
          boxShadow: 'focus',
        },
        _active: {
          bg: 'interaction.surfaceActive',
        },
      }
    : {};

  return (
    <Box
      {...linkProps}
      display="flex"
      minHeight={11}
      borderColor="separator.border"
      borderWidth="1px"
      borderStyle="solid"
      borderRadius="sm"
      overflow="hidden"
      transition="0.2s ease"
      width="100%"
    >
      <Box width={11}>
        <ThumbnailContainer>
          <PartThumbnail
            containsVirus={containsVirus}
            filename={filename}
            partFileUuid={primaryFileUuid}
          />
          {customWarningText && (
            <ThumbnailWarning
              color={customWarningColor ? customWarningColor : 'alert.error'}
              text={customWarningText}
            />
          )}
          {isAssembly && <AssemblyHint />}
        </ThumbnailContainer>
      </Box>
      <Box
        display="flex"
        flexDir="column"
        flex="1 0 0"
        width="calc(100% - 88px)"
        maxWidth="calc(100% - 88px)"
      >
        {isExportControlled && (
          <CellWarning color="alert.error" text="Export Controlled" />
        )}
        <Box
          display="flex"
          flexDir="column"
          pl={2}
          pr={2}
          margin="auto 0"
          flexGrow={0}
        >
          {partAndRev}
          <Box>
            <Text
              color="feature.secondary"
              fontFamily="data"
              fontSize="sm"
              whiteSpace="normal"
              wordBreak="break-all"
            >
              {partName ? (
                <HighlightOrDisplay
                  result={partResult}
                  field="partName"
                  nonHighlightTextColor="feature.secondary"
                  useDataFont
                />
              ) : (
                '--'
              )}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PartCell;
